import { Intent } from "@blueprintjs/core";
import classNames from "classnames";
import { Button } from "components/buttons";
import { ShowHide } from "components/layout";
import { DocumentViewer } from "components/media";
import { NotificationInline, NotificationToaster } from "components/notifications";
import { UserAccess } from "components/user-access";
import { HelperFunctions } from "helpers";
import { PageRestricted } from "pages/errors/page-restricted";
import { PolicyDocumentTypes } from "pages/policy/document/constants/PolicyDocumentTypes";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { EmployeeService } from "services";
import { formatDocumentName } from "utils/formatDocumentName";

export function ViewDocument({ documentName, documentStorageIdentifier, onLoading, documentType, showTitle = true }) {

    const [loading, setLoading] = useState(true);
    const [document, setDocument] = useState(null);
    const [downloadingInProgress, setDownloadingInProgress] = useState(false);
    const [invalidDocument, setInvalidDocument] = useState(false);
    const [url, setUrl] = useState("");
    const [mimeType, setMimeType] = useState("");
    const requiredAction = "Documents:View";
    const noAccessCode = 403;    

    useEffect(() => {
        if (documentStorageIdentifier) {
            beginLoadingDocument();

            const downloadFunction = documentType === PolicyDocumentTypes.SIGNED
                ? EmployeeService.downloadSignedPolicyDocument
                : EmployeeService.downloadUnsignedPolicyDocument;

            downloadFunction(documentStorageIdentifier)
                .then((blob) => {
                    setDocument(blob);
                    setUrl(URL.createObjectURL(blob));
                    setMimeType(blob.type);
                })
                .catch((error) => {
                    if (error.status === noAccessCode) {
                        setInvalidDocument(true);                        
                    } else {
                        NotificationToaster.show(Intent.DANGER, "Could not the load document. Please try again.");
                    }  
                })
                .finally(() => {
                    finishLoadingDocument();
                });
        }
    }, [documentStorageIdentifier, documentType]);

    function downloadFile() {
        setDownloadingInProgress(true);

        if (document) {
            NotificationToaster.show(Intent.SUCCESS, "Your file will be downloaded shortly", false);
            HelperFunctions.downloadFile(document, formatDocumentName(documentName));
        } else {
            NotificationToaster.show(Intent.DANGER, "Your file could not be downloaded.", false);
        }

        setDownloadingInProgress(false);
    }

    function beginLoadingDocument() {
        setLoading(true);

        if (onLoading) {
            onLoading(true);
        }
    }

    function finishLoadingDocument() {
        setLoading(false);

        if (onLoading) {
            onLoading(false);
        }
    }

    return (
        <Fragment>
            <UserAccess perform={requiredAction}
                yes={() => (
                    <div>
                        <ShowHide
                            evaluator={invalidDocument}
                            show={(
                                <div className="row">
                                    <NotificationInline
                                        allowClose={false}
                                        show
                                        text="You do not have permission to view this type"
                                        intent="danger">
                                    </NotificationInline>
                                </div>
                            )}
                            hide={(<>
                                <div>
                                    {showTitle && <h2 className={classNames({ 'bp6-skeleton': loading })}>{documentName}</h2>}
                                    <div className='button-row spacer-bottom'>
                                        <Button
                                            intent="primary"
                                            text={"Download"}
                                            icon="import"
                                            onClick={downloadFile}
                                            disabled={loading || downloadingInProgress}
                                        />
                                    </div>
                                    <h2>
                                        Document
                                    </h2>
                                </div>
                                <div>
                                    <DocumentViewer
                                        url={url}
                                        limitMediaHeight={true}
                                        iframeHeight="500px"
                                        loading={loading}
                                        mimeType={mimeType}
                                        isDownloading={downloadingInProgress}
                                        downloadFile={downloadFile} />
                                </div>
                            </>)}
                        />
                    </div>
                )}
                no={() => (
                    <PageRestricted />
                )}
            />
        </Fragment>
    );
}

ViewDocument.propTypes = {
    documentName: PropTypes.string.isRequired,
    documentStorageIdentifier: PropTypes.string,
    onLoading: PropTypes.func,
    documentType: PropTypes.oneOf([PolicyDocumentTypes.SIGNED, PolicyDocumentTypes.UNSIGNED]).isRequired,
    showTitle: PropTypes.bool,
};